import { create } from "zustand";

const defaultEditContractSteps = [
    {
        id: "edit-contract",
        title: "Edit contract",
        description: "Modify existing contract details.",
        isPrepared: false,
    },
];

const defaultDuplicateContractSteps = [
    {
        id: "duplicate-contract",
        title: "Duplicate contract",
        description:
            "Create a new contract by duplicating the selected contract as a starting point.",
        isPrepared: false,
    },
];

const defaultCopyContractSteps = [
    {
        id: "copy-contract",
        title: "Copy contract",
        description:
            "Create a new contract by copying the selected contract as a starting point.",
        isPrepared: false,
    },
];

const defaultUploadDocumentSteps = [
    {
        id: "upload-file",
        title: "Upload file",
        description: "Upload a document file to attach it to the contract.",
        isPrepared: false,
    },
];

const defaultViewContractSteps = [
    {
        id: "view-contract",
        title: "View contract",
        description: "View the contract details in a read‑only format.",
        isPrepared: false,
    },
];

const defaultAddProductsSteps = [
    {
        id: "add-products",
        title: "Add products",
        description: "Select and add products to include in the contract.",
        isPrepared: false,
    },
];

const defaultAddScalesSteps = [
    {
        id: "add-scales",
        title: "Add scale version",
        description: "Define new scale version associated with the contract.",
        isPrepared: false,
    },
];

const defaultMaintainCompensationComponentsSteps = [
    {
        id: "maintain-compensation-components",
        title: "Maintain Compensation Components",
        description:
            "Manage and update compensation elements linked to the contract.",
        isPrepared: false,
    },
];

interface ContractState {
    viewSteps: typeof defaultViewContractSteps;
    setViewSteps: (steps: any) => void;

    editSteps: typeof defaultEditContractSteps;
    setEditSteps: (steps: any) => void;

    duplicateSteps: typeof defaultDuplicateContractSteps;
    setDuplicateSteps: (steps: any) => void;

    uploadDocumentSteps: typeof defaultUploadDocumentSteps;
    setUploadDocumentSteps: (steps: any) => void;

    addProductsSteps: typeof defaultAddProductsSteps;
    setAddProductsSteps: (steps: any) => void;

    addScalesSteps: typeof defaultAddScalesSteps;
    setAddScalesSteps: (steps: any) => void;

    copySteps: typeof defaultCopyContractSteps;
    setCopySteps: (steps: any) => void;

    maintainCompensationComponentsSteps: typeof defaultMaintainCompensationComponentsSteps;
    setMaintainCompensationComponentsSteps: (steps: any) => void;

    resetContractData: () => void;
}

export const useContractStore = create<ContractState>(set => ({
    viewSteps: defaultViewContractSteps,
    setViewSteps: viewSteps => set({ viewSteps }),

    editSteps: defaultEditContractSteps,
    setEditSteps: editSteps => set({ editSteps }),

    duplicateSteps: defaultDuplicateContractSteps,
    setDuplicateSteps: duplicateSteps => set({ duplicateSteps }),

    copySteps: defaultCopyContractSteps,
    setCopySteps: copySteps => set({ copySteps }),

    uploadDocumentSteps: defaultUploadDocumentSteps,
    setUploadDocumentSteps: uploadDocumentSteps => set({ uploadDocumentSteps }),

    addProductsSteps: defaultAddProductsSteps,
    setAddProductsSteps: addProductsSteps => set({ addProductsSteps }),

    addScalesSteps: defaultAddScalesSteps,
    setAddScalesSteps: addScalesSteps => set({ addScalesSteps }),

    maintainCompensationComponentsSteps:
        defaultMaintainCompensationComponentsSteps,
    setMaintainCompensationComponentsSteps:
        maintainCompensationComponentsSteps =>
            set({ maintainCompensationComponentsSteps }),

    resetContractData: () =>
        set({
            viewSteps: defaultViewContractSteps,
            editSteps: defaultEditContractSteps,
            duplicateSteps: defaultDuplicateContractSteps,
            uploadDocumentSteps: defaultUploadDocumentSteps,
            setViewSteps: () => set({ viewSteps: defaultViewContractSteps }),
            setEditSteps: () => set({ editSteps: defaultEditContractSteps }),
            setDuplicateSteps: () =>
                set({ duplicateSteps: defaultDuplicateContractSteps }),
            setUploadDocumentSteps: () =>
                set({ uploadDocumentSteps: defaultUploadDocumentSteps }),
            setCopySteps: () => set({ copySteps: defaultCopyContractSteps }),
        }),
}));
